/* Atelier Services — stacked single-column with italic prices in
   serif. Hairline rules between. A typeset menu, not a grid. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

function priceFrom(svc) {
  const tiers = Array.isArray(svc?.tiers) ? svc.tiers : [];
  const tp = tiers.map(t => Number(t?.price)).filter(n => Number.isFinite(n) && n > 0);
  if (tp.length) return Math.min(...tp);
  const n = Number(svc?.price_from ?? svc?.starting_price ?? svc?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function AtelierServices() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [services, setServices] = useState(null);
  useEffect(() => {
    api.get("/services").then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => setServices([]));
    document.title = `${studioName} — The Sittings`;
  }, [studioName]);

  if (services === null) return <EditorialSkeleton eyebrow="Drawing the menu." />;

  return (
    <div data-testid="atelier-services" style={{ background: "var(--tenant-bg, #FEF5F2)", color: "var(--tenant-fg, #3A1B22)" }}>
      <section className="px-6 lg:px-12 pt-20 pb-12 text-center">
        <div className="text-[10px] uppercase tracking-[0.42em] mb-5" style={{ color: "var(--tenant-accent)" }}>
          The Sittings
        </div>
        <h1 className="italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
          A small menu, carefully kept.
        </h1>
      </section>

      <div className="max-w-2xl mx-auto px-6 pb-32">
        <ul>
          {services.map((svc) => {
            const p = priceFrom(svc);
            return (
              <li key={svc.id} className="py-10 border-t" id={svc.id || svc.slug}
                  style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 25%, transparent)" }}>
                <div className="flex items-baseline justify-between gap-6 mb-3">
                  <h2 className="italic text-3xl" style={{ fontFamily: "var(--tenant-serif)" }}>{svc.name}</h2>
                  {p !== null && (
                    <span className="italic text-2xl whitespace-nowrap" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
                      from ${p.toFixed(0)}
                    </span>
                  )}
                </div>
                {(svc.description || svc.short_description) && (
                  <p className="text-base leading-loose italic max-w-md" style={{ color: "var(--tenant-fg-soft)" }}>
                    {svc.description || svc.short_description}
                  </p>
                )}
                <Link to={tenantHref("/booking")}
                  className="inline-block mt-5 text-[10px] uppercase tracking-[0.32em] pb-0.5 border-b"
                  style={{ color: "var(--tenant-accent)", borderColor: "var(--tenant-accent)" }}>
                  Reserve this sitting →
                </Link>
              </li>
            );
          })}
          {services.length === 0 && (
            <li className="py-12 text-center italic" style={{ color: "var(--tenant-fg-soft)" }}>The menu is being set. Please write us.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
